import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch, useAppSelector } from '../store';
import { fetchProjects } from '../store/feature/projectSlice';
import BASE_URL from '../constants/Config';
import { useNavigate } from 'react-router-dom';
import LineWobble from '../components/Loader';

// Slugify fonksiyonu
const slugify = (text: string) => {
  return text
    .toLowerCase()
    .replace(/ /g, '-')
    .replace(/[^\w-]+/g, '');
};

function Projects() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { projects, loading, error, totalCount, page, pageSize } = useAppSelector(
    (state) => state.project
  );

  const [currentPage, setCurrentPage] = useState(page || 1);
  const [itemsPerPage, setItemsPerPage] = useState(pageSize || 6); // Varsayılanı 6 yaptık

  useEffect(() => {
    dispatch(fetchProjects({ page: currentPage, pageSize: itemsPerPage }));
  }, [dispatch, currentPage, itemsPerPage]);

  const truncateText = (text: string, maxLength: number) =>
    text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;

  const getPhotoUrl = (filePath: string) => {
    const fileName = filePath.split('/').pop();
    return `${BASE_URL}/api/Project/photo/${fileName}`;
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handlePageChange = (pageNumber: number) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-gray-900 text-white py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6">Referanslarımız</h1>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {loading ? (
              <div className="flex justify-center items-center w-full h-64 col-span-6">
                <LineWobble />
              </div>
            ) : error ? (
              <p className="text-red-500 col-span-6">{error}</p>
            ) : projects.length > 0 ? (
              projects.map((project) => (
                <div 
                  key={project.id} 
                  className="bg-white rounded-lg shadow-lg overflow-hidden flex flex-col"
                >
                  <img
                    src={getPhotoUrl(project.photoUrl)}
                    alt={project.title}
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-4 flex-grow">
                    <h3 className="text-lg font-bold mb-2 line-clamp-2">{project.title}</h3>
                    <p className="text-gray-600 text-sm line-clamp-3">
                      {truncateText(project.content, 100)}
                    </p>
                  </div>
                  <div className="p-4">
                    <button
                      onClick={() => navigate(`/projects/${slugify(project.title)}`)}
                      className="text-blue-600 text-sm font-semibold hover:text-blue-800"
                    >
                      Devamını Gör →
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="col-span-6">Henüz proje bulunmamaktadır.</p>
            )}
          </div>
        </div>
      </section>

      {/* Pagination Controls */}
      <section className="flex flex-col items-center py-6 space-y-4">
        <div className="flex justify-between items-center w-full max-w-4xl px-4">
          <div>
            <label htmlFor="itemsPerPage" className="mr-2">Sayfa Boyutu:</label>
            <select
              id="itemsPerPage"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="border rounded px-2 py-1"
            >
              <option value={6}>6</option>
              <option value={12}>12</option>
              <option value={18}>18</option>
            </select>
          </div>
          <p>Toplam {totalCount} Proje</p>
        </div>

        <div className="flex items-center justify-center space-x-4">
          <button
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
            className="px-4 py-2 bg-gray-500 text-white rounded disabled:bg-gray-300"
          >
            Geri
          </button>
          <span className="text-center">
            Sayfa {currentPage} / {totalPages}
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
            className="px-4 py-2 bg-gray-500 text-white rounded disabled:bg-gray-300"
          >
            İleri
          </button>
        </div>
      </section>
    </div>
  );
}

export default Projects;